import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { api } from "./apiClient";
import type { Project } from "@shared/types";

interface ProjectContextValue {
  projects: Project[];
  selectedProjectId: string | null;
  selectProject: (id: string) => void;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

const ProjectContext = createContext<ProjectContextValue | null>(null);

const LAST_PROJECT_KEY = "fieldbook:lastProjectId";

export function ProjectProvider({ children }: { children: ReactNode }) {
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(
    () => localStorage.getItem(LAST_PROJECT_KEY),
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get<{ projects: Project[] }>("/api/projects")
      .then((data) => {
        if (cancelled) return;
        setProjects(data.projects);
        setError(null);
        setSelectedProjectId((prev) => {
          if (prev && data.projects.some((p) => p.id === prev)) return prev;
          return data.projects[0]?.id ?? null;
        });
      })
      .catch((err) => !cancelled && setError(err.message || "Failed to load projects."))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [reloadToken]);

  const selectProject = useCallback((id: string) => {
    setSelectedProjectId(id);
    localStorage.setItem(LAST_PROJECT_KEY, id);
  }, []);

  const refresh = useCallback(() => setReloadToken((t) => t + 1), []);

  return (
    <ProjectContext.Provider value={{ projects, selectedProjectId, selectProject, loading, error, refresh }}>
      {children}
    </ProjectContext.Provider>
  );
}

export function useProjects(): ProjectContextValue {
  const ctx = useContext(ProjectContext);
  if (!ctx) throw new Error("useProjects must be used within ProjectProvider");
  return ctx;
}
